import PouchDB from "pouchdb";
import { SettingItem } from "./types/settings";
import { ProjectItem } from "./types/projects";
import { FormTemplateItem } from "./types/formTemplates";
import { ImageObject, InventoryItem } from "./types/inventories";
import config from "../../config";
import _ from "lodash";
import { isPouchError } from "./common";
import { isLegacyImageObject, isModernImageObject, resize } from "../../utils/image";
import * as uuid from "uuid";
import * as upsert from "../../3rdparty/pouchdb-upsert";
import { TypesafeEventTarget } from "../../utils/TypesafeEventTarget";

PouchDB.plugin(upsert);

export type DatabaseIdentifier =
  | "settings"
  | "projects"
  | "formTemplates"
  | `inventory:${string}`
  | `inventoryImages:${string}`
  | `inventoryThumbnails${string}`;

async function dataUriToBlob(uri: string) {
  const resp = await fetch(uri);
  return await resp.blob();
}

type DatabasePair<T = {}> = { local: PouchDB.Database<T>; remote: PouchDB.Database<T> };
class PairedPouchDatabaseManager extends TypesafeEventTarget<EventDetail> {
  private readonly _options: PouchDB.Configuration.DatabaseConfiguration;
  private readonly _name: string;

  get name() {
    return this._name;
  }

  private _localName(subName?: string) {
    return this._name + "-" + subName;
  }
  private _remoteName(subName: string) {
    return config.couchdbBasePath + "/" + this._name + "/" + subName;
  }

  protected makeDatabasePair<T extends {} = {}>(name: string) {
    return {
      local: new PouchDB<T>(this._localName(name), this._options),
      remote: new PouchDB<T>(this._remoteName(name), this._options),
    };
  }

  protected syncPair<T extends {}>(pair: DatabasePair<T>, options?: PouchDB.Replication.SyncOptions) {
    return new Promise<PouchDB.Replication.SyncResultComplete<{}>>((resolve, reject) =>
      pair.local.sync(pair.remote, options).on("error", reject).on("complete", resolve)
    );
  }
  protected replicateToRemotePair<T extends {}>(
    pair: DatabasePair<T>,
    options?: PouchDB.Replication.ReplicateOptions,
    onChange?: () => void
  ) {
    return this.replicateToLocalPair({ remote: pair.local, local: pair.remote }, options, onChange);
  }
  protected replicateToLocalPair<T extends {}>(
    pair: DatabasePair<T>,
    options?: PouchDB.Replication.ReplicateOptions,
    onChange?: () => void
  ) {
    const replication = pair.remote.replicate.to(pair.local, options);
    if (onChange) {
      replication.on("change", onChange);
    }
    return {
      replication,
      promise: new Promise<PouchDB.Replication.ReplicationResultComplete<{}>>((resolve, reject) => {
        replication.on("error", reject).on("complete", resolve);
      }),
    };
  }

  constructor(name: string, options: PouchDB.Configuration.DatabaseConfiguration) {
    super();
    this._name = name;
    this._options = options;
  }
}

function tryLoadLocalStorageJSON(key: string) {
  const raw = localStorage.getItem(key);
  if (raw) {
    return JSON.parse(raw);
  } else {
    return undefined;
  }
}

export class PouchDatabaseManager extends PairedPouchDatabaseManager {
  private readonly _settingsDatabase: DatabasePair<SettingItem>;
  private readonly _projectsDatabase: DatabasePair<ProjectItem>;
  private readonly _formTemplatesDatabase: DatabasePair<FormTemplateItem>;
  private _inventories: Record<
    string,
    {
      inventory: DatabasePair<InventoryItem>;
      inventoryImages: DatabasePair;
      inventoryThumbnails: DatabasePair;
    }
  > = {};

  get databases() {
    return {
      settings: this._settingsDatabase.local,
      projects: this._projectsDatabase.local,
      formTemplates: this._formTemplatesDatabase.local,
    };
  }

  constructor(name: string, options: PouchDB.Configuration.DatabaseConfiguration) {
    super(name, options);

    this._settingsDatabase = this.makeDatabasePair<SettingItem>("settings");
    this._projectsDatabase = this.makeDatabasePair<ProjectItem>("projects");
    this._formTemplatesDatabase = this.makeDatabasePair<FormTemplateItem>("formtemplates");

    this._dirty = tryLoadLocalStorageJSON(`piano-${name}-dirty`) || [];
    this._lastSync = _.mapValues(tryLoadLocalStorageJSON(`piano-${name}-lastSync`) || {}, (v) => new Date(v));
  }

  jwtChanged() {
    if (this._registeredQueries.length === 0) {
      return;
    }
    const inventories = this._registeredQueries
      .flatMap(([dbs, _]) => dbs)
      .filter((db) => db.startsWith("inventory"))
      .map((db) => db.split(":")[1])
      .filter((inventory, index, list) => index === list.indexOf(inventory));
    this.sync(inventories).then(() => {});
  }

  getInventory(name: string) {
    if (!(name in this._inventories)) {
      this._inventories[name] = {
        inventory: this.makeDatabasePair<InventoryItem>(name),
        inventoryImages: this.makeDatabasePair(name + "_images"),
        inventoryThumbnails: this.makeDatabasePair(name + "_thumbnails"),
      };
      this.dispatch("inventory:add", { name });
    }
    return {
      inventory: this._inventories[name].inventory.local,
      inventoryImages: this._inventories[name].inventoryImages.local,
      inventoryThumbnails: this._inventories[name].inventoryThumbnails.local,
    };
  }

  get loadedInventories() {
    return Object.keys(this._inventories);
  }

  private _syncingInventories: string[] | null = null;
  private _lastSyncError: unknown | null = null;
  private _haveSyncedOnce: boolean = false;
  private _haveSyncedInventoriesOnce: string[] = [];
  private _dirty: DatabaseIdentifier[] = [];
  private _lastSync: Partial<Record<DatabaseIdentifier, Date>> = {};

  get syncingInventories() {
    return this._syncingInventories;
  }
  get dirty() {
    return this._dirty;
  }
  get lastSync() {
    return this._lastSync;
  }
  get lastSyncError() {
    return this._lastSyncError;
  }

  markDirty(database: DatabaseIdentifier) {
    if (!this._dirty.includes(database)) {
      this._dirty.push(database);
      localStorage.setItem(`piano-${this.name}-dirty`, JSON.stringify(this._dirty));
      this.dispatch("dirty:set", { database });
    }
  }
  clearDirty(database: DatabaseIdentifier) {
    if (this._dirty.includes(database)) {
      this._dirty.splice(this._dirty.indexOf(database), 1);
      localStorage.setItem(`piano-${this.name}-dirty`, JSON.stringify(this._dirty));
      this.dispatch("dirty:clear", { database });
    }
  }

  syncDownContinuous(inventory: string) {
    const replications = [
      this.replicateToLocalPair(this._settingsDatabase, { live: true }, () => this.notifyNeedReload(["settings"], false)),
      this.replicateToLocalPair(this._projectsDatabase, { live: true }, () => this.notifyNeedReload(["projects"], false)),
      this.replicateToLocalPair(this._formTemplatesDatabase, { live: true }, () => this.notifyNeedReload(["formTemplates"], false)),
      this.replicateToLocalPair(this._inventories[inventory].inventory, { live: true }, () =>
        this.notifyNeedReload([`inventory:${inventory}`], false)
      ),
    ];

    return () => {
      replications.forEach((r) => r.replication.cancel());
    };
  }
  async syncUp(databases: DatabaseIdentifier[]) {
    const inventories = databases.filter((d) => d.startsWith("inventory:")).map((d) => d.replace("inventory:", ""));
    this.dispatch("sync:start", { inventories });
    try {
      const results = await Promise.all(
        databases
          .flatMap((d): [DatabaseIdentifier, DatabasePair][] => {
            if (d === "projects") {
              return [[d, this._projectsDatabase]];
            } else if (d === "formTemplates") {
              return [[d, this._formTemplatesDatabase]];
            } else if (d === "settings") {
              return [[d, this._settingsDatabase]];
            } else if (d.startsWith("inventory:")) {
              return Object.values(this._inventories[d.replace("inventory:", "")]).map((pair) => [d, pair]);
            } else {
              throw new Error("Something went wrong");
            }
          })
          .map(
            async ([database, pair]): Promise<[DatabaseIdentifier, PouchDB.Replication.ReplicationResultComplete<{}>]> => [
              database,
              await this.replicateToRemotePair(pair).promise,
            ]
          )
      );
      this.dispatch("sync:success", { inventories });

      results.forEach(([database, _result]) => {
        this.clearDirty(database);
      });
    } catch (e) {
      this.dispatch("sync:error", { inventories, error: e });
      return false;
    } finally {
      this.dispatch("sync:finished", { inventories });
    }
    return true;
  }

  async sync(inventories: string[]) {
    // don't run the same sync in parallell more than once
    if (_.isEqual(inventories, this._syncingInventories)) {
      return new Promise((resolve, reject) => {
        this.on("sync:error", reject);
        this.on("sync:success", resolve);
      });
    }

    console.log("[POUCH SYNC] db:", this.name, "inventories:", inventories.join(","));
    this._syncingInventories = inventories;
    this._lastSyncError = null;
    this._haveSyncedOnce = true;
    this._haveSyncedInventoriesOnce = _.uniq([...this._haveSyncedInventoriesOnce, ...inventories]);
    this.dispatch("sync:start", { inventories });
    try {
      const [settingsResult, projectsResult, formTemplatesResult] = await Promise.all([
        this.syncPair(this._settingsDatabase),
        this.syncPair(this._projectsDatabase),
        this.syncPair(this._formTemplatesDatabase),
      ]);
      this.clearDirty("projects");
      this.clearDirty("settings");
      this.clearDirty("formTemplates");
      this._lastSync["projects"] = new Date();
      this._lastSync["settings"] = new Date();
      this._lastSync["formTemplates"] = new Date();
      const inventoryResults = await Promise.all(
        inventories.map((i) =>
          Promise.all([
            Promise.resolve(i),
            this.syncPair(this._inventories[i].inventory),
            // images are always only "synced" to the remote, otherwise the client databases might grow unreasonable large.
            // fetching will instead be done on individual images.
            // something like https://github.com/pouchdb/pouchdb/issues/4362 would have been ideal, but does not currently exist.
            this.replicateToRemotePair(this._inventories[i].inventoryImages).promise,
            this.replicateToRemotePair(this._inventories[i].inventoryThumbnails).promise,
          ])
        )
      );
      for (const inventory of inventories) {
        this.clearDirty(`inventory:${inventory}`);
        this.clearDirty(`inventoryImages:${inventory}`);
        this.clearDirty(`inventoryThumbnails:${inventory}`);
        this._lastSync[`inventory:${inventory}`] = new Date();
        this._lastSync[`inventoryImages:${inventory}`] = new Date();
        this._lastSync[`inventoryThumbnails:${inventory}`] = new Date();
      }
      localStorage.setItem(`piano-${this.name}-lastSync`, JSON.stringify(_.mapValues(this._lastSync, (v) => v?.toISOString())));
      this.dispatch("sync:success", { inventories });

      const changedDatabases: DatabaseIdentifier[] = [];
      if (settingsResult.pull!.docs_written > 0) {
        changedDatabases.push("settings");
      }
      if (projectsResult.pull!.docs_written > 0) {
        changedDatabases.push("projects");
      }
      if (formTemplatesResult.pull!.docs_written > 0) {
        changedDatabases.push("formTemplates");
      }
      for (const [inventoryId, d1, _d2, _d3] of inventoryResults) {
        if (d1.pull!.docs_written > 0) {
          changedDatabases.push(`inventory:${inventoryId}`);
        }
      }
      this.notifyNeedReload(changedDatabases, false);
    } catch (e) {
      this._lastSyncError = e;
      this.dispatch("sync:error", { inventories, error: e });
      throw e;
    } finally {
      this._syncingInventories = null;
      this.dispatch("sync:finished", { inventories });
    }
  }

  private _registeredQueries: [DatabaseIdentifier[], () => void][] = [];

  registerQuery(databases: DatabaseIdentifier[], callback: () => void) {
    this._registeredQueries.push([databases, callback]);

    const missingInventories = databases
      .filter((d) => d.startsWith("inventory"))
      .map((d) => d.split(":")[1])
      .filter((i) => !this._haveSyncedInventoriesOnce.includes(i));
    if (missingInventories.length > 0 || !this._haveSyncedOnce) {
      this.sync(missingInventories).then(() => {});
    }
  }
  unregisterQuery(databases: DatabaseIdentifier[], callback: () => void) {
    const idx = this._registeredQueries.indexOf([databases, callback]);
    if (idx >= 0) {
      this._registeredQueries.splice(idx, 1);
    }
  }

  /**
   * Called by hooks that might modify data, tells other hooks that use the same database that they might need to reload
   * to get the latest data.
   *
   * Also, optionally (by default), marks the databases passed as dirty.
   */
  notifyNeedReload(databases: DatabaseIdentifier[], markDirty: boolean = true) {
    for (const [dbs, callback] of this._registeredQueries) {
      if (dbs.some((d) => databases.includes(d))) {
        callback();
      }
    }
    if (markDirty) {
      for (const db of databases) {
        this.markDirty(db);
      }
    }
  }

  /**
   * Attempts to get the image attached to the specified document.
   *
   * First tries the local database, if nothing found tries the remote database, if again nothing found returns null.
   *
   * If a different kind of error occurs it will be rethrown.
   */
  async _getImage(id: string, pair: DatabasePair) {
    try {
      return await pair.local.getAttachment(id, "image.jpg");
    } catch (e) {
      if (isPouchError(e) && e.name === "not_found" && e.message === "missing") {
        try {
          return await pair.remote.getAttachment(id, "image.jpg");
        } catch (e) {
          if (isPouchError(e)) {
            return null;
          } else {
            throw e;
          }
        }
      } else {
        throw e;
      }
    }
  }
  async getImage(inventoryId: string, id: string) {
    return await this._getImage(id, this._inventories[inventoryId].inventoryImages);
  }
  async getThumbnail(inventoryId: string, id: string) {
    return await this._getImage(id, this._inventories[inventoryId].inventoryThumbnails);
  }
  async saveImage(inventoryId: string, formId: string, obj: ImageObject): Promise<ImageObject> {
    if (isModernImageObject(obj)) {
      return obj;
    } else if (isLegacyImageObject(obj)) {
      // restructure and resave legacy image object
      return await this.saveImage(inventoryId, formId, { imageUri: obj.src, thumbnailUri: await resize(obj.src, 120) });
    }
    const inventoryDatabases = this._inventories[inventoryId];
    const id = `${formId}_${uuid.v4()}`;
    const savedImage = await inventoryDatabases.inventoryImages.local.put({
      _id: id,
      _attachments: {
        "image.jpg": {
          content_type: "image/jpeg",
          data: await dataUriToBlob(obj.imageUri),
        },
      },
    });
    const savedThumbnail = await inventoryDatabases.inventoryThumbnails.local.put({
      _id: id,
      _attachments: {
        "image.jpg": {
          content_type: "image/jpeg",
          data: await dataUriToBlob(obj.thumbnailUri),
        },
      },
    });
    return {
      imageId: savedImage.id,
      thumbnailId: savedThumbnail.id,
    };
  }
}

interface EventDetail {
  // databases for an inventory where loaded
  "inventory:add": { name: string };
  // syncing started
  "sync:start": { inventories: string[] };
  // syncing finished successfully
  "sync:success": { inventories: string[] };
  // syncing failed
  "sync:error": { inventories: string[]; error: unknown };
  // syncing finished (either successfully or with error)
  "sync:finished": { inventories: string[] };
  // a database was marked as being dirty (having local changes)
  "dirty:set": { database: DatabaseIdentifier };
  // the dirty state of a database was cleared (changes where upstreamed)
  "dirty:clear": { database: DatabaseIdentifier };
}

export class PouchManager extends TypesafeEventTarget<{ "changed:jwt": { jwt: string | null } }> {
  private readonly _databases: Record<string, PouchDatabaseManager> = {};
  private _jwt: string | null = null;

  get jwt(): string | null {
    return this._jwt;
  }

  set jwt(jwt: string | null) {
    if (jwt === this._jwt) {
      return;
    }
    this._jwt = jwt;
    this.dispatch("changed:jwt", { jwt });
  }

  getDatabase(name: string) {
    if (!(name in this._databases)) {
      this._databases[name] = new PouchDatabaseManager(name, {
        fetch: (url, opts) => {
          if (!this._jwt) {
            return Promise.reject(
              new Response("No JWT has been specified. This is a client-side error, no network request was performed.", { status: 401 })
            );
          }
          (opts!.headers as any).set("Authorization", "Bearer " + this._jwt);
          return PouchDB.fetch(url, opts);
        },
      });
      this.on("changed:jwt", () => this._databases[name].jwtChanged());
    }
    return this._databases[name];
  }
}
